import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";

import { blogIndexQuery } from "@/lib/blog/queries";
import { BlogBanner } from "@/components/blog/BlogBanner";
import { PostCard } from "@/components/blog/PostCard";
import { BlackDestaqueSection } from "@/components/black2x1/BlackDestaqueSection";
import { CategoryStrip } from "@/components/blog/CategoryStrip";
import { Pagination } from "@/components/blog/Pagination";
import { descricaoCategoria } from "@/lib/blog/categoria-descricoes";
import { normalizarPagina, paginar, totalPaginas } from "@/lib/blog/leitura";

const BASE = "https://fisioemortopedia.com.br";

export const Route = createFileRoute("/e-fisio/categoria/$slug")({
  validateSearch: (search: Record<string, unknown>) => {
    const n = Number(search.page);
    return { page: Number.isFinite(n) && n > 1 ? Math.trunc(n) : undefined } as { page?: number };
  },
  loaderDeps: ({ search }) => ({ page: search.page }),
  loader: async ({ context, params, deps }) => {
    const data = await context.queryClient.ensureQueryData(blogIndexQuery);
    const cat = data.categories.find((c) => c.slug === params.slug);
    if (!cat) throw notFound();
    const posts = data.posts.filter((p) => p.categoria_slugs?.includes(cat.slug));
    return {
      nome: cat.nome,
      slug: cat.slug,
      total: posts.length,
      page: normalizarPagina(deps.page ?? 1, posts.length),
      itens: posts.slice(0, 12).map((p) => ({ slug: p.slug, titulo: p.titulo })),
    };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Categoria não encontrada · E-FISIO" }, { name: "robots", content: "noindex" }],
      };
    }
    const page = loaderData.page;
    const base = `${BASE}/e-fisio/categoria/${loaderData.slug}`;
    const url = page > 1 ? `${base}?page=${page}` : base;
    const title =
      page > 1
        ? `${loaderData.nome} · Artigos E-FISIO (página ${page})`
        : `${loaderData.nome} · Artigos E-FISIO`;
    const description = descricaoCategoria(loaderData.slug, loaderData.nome);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: title,
            description,
            url,
            inLanguage: "pt-BR",
            isPartOf: { "@type": "Blog", name: "E-FISIO", url: `${BASE}/e-fisio` },
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: loaderData.total,
              itemListElement: loaderData.itens.map((p, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: p.titulo,
                url: `${BASE}/e-fisio/${p.slug}`,
              })),
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "E-FISIO", item: `${BASE}/e-fisio` },
              { "@type": "ListItem", position: 2, name: loaderData.nome, item: base },
            ],
          }),
        },
      ],
    };
  },
  errorComponent: CategoriaErro,
  notFoundComponent: CategoriaNaoEncontrada,
  component: CategoriaPage,
});

function CategoriaErro() {
  return (
    <main className="container mx-auto px-4 py-24 text-center">
      <h1 className="text-2xl font-extrabold text-[var(--efisio-navy)]">
        Não conseguimos carregar esta categoria
      </h1>
      <p className="mt-3 text-[var(--efisio-muted)]">Tente novamente em alguns instantes.</p>
    </main>
  );
}

function CategoriaNaoEncontrada() {
  return (
    <main className="container mx-auto px-4 py-24 text-center">
      <h1 className="text-2xl font-extrabold text-[var(--efisio-navy)]">Categoria não encontrada</h1>
      <p className="mt-3 text-[var(--efisio-muted)]">
        Essa categoria não existe (ou ainda não tem artigos publicados).
      </p>
      <Link
        to="/e-fisio"
        className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[var(--efisio-blue)]"
      >
        <ArrowLeft className="h-4 w-4" /> Ver todos os artigos
      </Link>
    </main>
  );
}

function CategoriaPage() {
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const { data } = useSuspenseQuery(blogIndexQuery);
  const cat = data.categories.find((c) => c.slug === slug);
  const posts = data.posts.filter((p) => p.categoria_slugs?.includes(slug));

  const page = normalizarPagina(search.page ?? 1, posts.length);
  const totalPages = totalPaginas(posts.length);
  const visiveis = paginar(posts, page);
  const nome = cat?.nome ?? slug;

  return (
    <main>
      <BlogBanner />
      <section className="container mx-auto px-4 pt-10 sm:pt-14">
        <div className="max-w-3xl">
          <nav aria-label="Trilha de navegação">
            <Link
              to="/e-fisio"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--efisio-blue)]"
            >
              <ArrowLeft className="h-4 w-4" /> Todos os artigos
            </Link>
          </nav>
          <h1 className="mt-4 text-3xl font-extrabold uppercase leading-tight text-[var(--efisio-navy)] sm:text-4xl">
            {nome}
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-[var(--efisio-muted)] sm:text-base">
            {descricaoCategoria(slug, nome)}
          </p>
          <p className="mt-3 text-xs uppercase tracking-wider text-[var(--efisio-muted)]">
            {posts.length} {posts.length === 1 ? "artigo publicado" : "artigos publicados"}
            {totalPages > 1 && ` · página ${page} de ${totalPages}`}
          </p>
        </div>
      </section>

      <section className="container mx-auto px-4 py-10 sm:py-14">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-12">
          <div>
            <h2 className="sr-only">Artigos da categoria {nome}</h2>

            {visiveis.length === 0 ? (
              <p className="rounded-2xl border border-[var(--efisio-border)] bg-[var(--efisio-surface)] p-8 text-center text-[var(--efisio-muted)]">
                Nenhum artigo publicado nesta categoria por enquanto.
              </p>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2">
                {visiveis.map((post, i) => (
                  <PostCard
                    key={post.slug}
                    post={post}
                    categories={data.categories}
                    priority={i === 0}
                  />
                ))}
              </div>
            )}

            <div className="mt-10 overflow-hidden rounded-2xl">
              <BlackDestaqueSection origem="efisio-categoria" />
            </div>

            <Pagination
              page={page}
              totalPages={totalPages}
              renderLink={(p, children, className) => (
                <Link
                  key={String(children)}
                  to="/e-fisio/categoria/$slug"
                  params={{ slug }}
                  search={p > 1 ? { page: p } : {}}
                  className={className}
                >
                  {children}
                </Link>
              )}
            />
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <CategoryStrip
              categories={data.categories}
              posts={data.posts}
              atual={slug}
              ocultarAtual
              titulo="Outras áreas"
            />
          </aside>
        </div>
      </section>
    </main>
  );
}
